import { CSSProperties, useEffect, useState } from "react";
import {
  Button,
  Collapse,
  Input,
  message,
  Modal,
  Popover,
  Select,
  Table,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { getStatusColor } from "../../util/statusColor";
import TableAction from "../../components/TableAction";
import WithAuth from "../../hoc/WithAuth";
import { useGetProductById } from "../../api/product";
import {
  useFarmingStagesByProductId,
  useGetFarmingStageById,
  useAddProductFarmingStage,
  useGetFarmingStageList,
  useUpdateFarmingStage,
  useDisassociateFarmingStageFromProduct,
} from "../../api/farming_stage";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { MoreOutlined } from "@ant-design/icons";

const wrapper: CSSProperties = {
  display: "flex",
  width: "100%",
  gap: 10,
  fontSize: 16,
  marginBottom: 20,
  flexDirection: "column",
};
const label: CSSProperties = {
  color: "#128807",
  fontWeight: 500,
  fontSize: 20,
};
const value: CSSProperties = {
  color: "#8590A3",
  fontWeight: 500,
  fontSize: 16,
};

function ProductsDetailPage() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<any[]>([]);
  const { user } = useAuth() as AuthContextValue;
  const { id: product_id } = useParams();
  const { data: product } = useGetProductById(product_id!);
  const [selectedId, setSelectedId] = useState<number | undefined>();

  const [fStages, setFarmingStages] = useState<{ id: number; name: string }[]>(
    [],
  );
  const { data: _farmingStages } = useGetFarmingStageList();

  const [stageId, setStageId] = useState<number | undefined>();

  const {
    data: farmingStages,
    isLoading,
    refetch,
  } = useFarmingStagesByProductId(Number(product_id!));
  const { data: selectedStage } = useGetFarmingStageById(selectedId || 0);
  const {
    mutateAsync: addStage,
    isPending: creating,
    isSuccess: created,
  } = useAddProductFarmingStage();
  const {
    mutateAsync: updateStage,
    isPending: updating,
    isSuccess: updated,
  } = useUpdateFarmingStage();
  const {
    mutateAsync: deleteStage,
    isPending: deleting,
    isSuccess: deleted,
  } = useDisassociateFarmingStageFromProduct();

  const [searchText, setSearchText] = useState<string | undefined>();
  const [open, setOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [stageNumber, setStageNumber] = useState<number | undefined | null>();
  const [stageName, setStageName] = useState<string | undefined>();
  const [status, setStatus] = useState<"ACTIVE" | "ARCHIVED">("ACTIVE");
  const [tags, setTags] = useState<{ [x: string]: any }[]>([]);

  useEffect(() => {
    if (searchText) {
      const _allData = farmingStages?.data?.data.map((item: any) => {
        return { id: item.id, stageName: item.stageName };
      });

      const _data = _allData.filter((item: any) =>
        item.stageName?.toLowerCase().includes(searchText.toLowerCase()),
      );
      setTableData(_data);
    } else {
      if (farmingStages && farmingStages.data && farmingStages.data.data) {
        setTableData(farmingStages.data.data);
      }
    }
  }, [searchText, farmingStages]);

  useEffect(() => {
    if (
      product &&
      product.data &&
      product.data.data &&
      product.data.data.detail
    ) {
      if (product.data.data.detail === "{}") {
        setTags([]);
      } else {
        const _tags = JSON.parse(product.data.data.detail);
        setTags([_tags]);
      }
    }
  }, [product]);

  useEffect(() => {
    if (
      _farmingStages &&
      _farmingStages.data &&
      _farmingStages.data.data &&
      _farmingStages.data.data.data
    ) {
      const _data = _farmingStages.data.data.data.map((stage: any) => {
        return { id: stage.id, name: stage.stageName };
      });

      setFarmingStages(_data);
    }
  }, [_farmingStages]);

  if (!product) {
    return <div>Loading...</div>;
  }

  const handleCreate = () => {
    setOpen(true);
  };

  const stagesColumn = [
    {
      title: "No.",
      width: 20,
      render: (text: string, record: any, index: number) => (
        <div style={{ width: 300 }}>
          <b>#{index + 1}</b>
        </div>
      ),
    },
    {
      dataIndex: "stageName",
      title: "Stage name",
      render: (text: string) => <div style={{ width: 300 }}>{text}</div>,
    },
    {
      title: "Actions",
      render: (text: string, record: any, index: number) => (
        <div style={{ display: "flex", gap: 12, width: 300 }}>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteOpen(true);
              setSelectedId(record.id);
            }}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ];

  const hideModal = () => {
    setSelectedId(undefined);
    setOpen(false);
  };

  const onSubmit = async () => {
    if (stageId && product_id) {
      if (selectedId) {
        // const result = await updateStage({
        //   id: selectedId,
        //   stage: { name: stageName, description:}
        // });
      } else {
        try {
          const result = await addStage({
            productId: Number(product_id),
            farmingStageId: stageId,
          });

          if (result && result.status === 200) {
            message.success("Add farming stage successfully.");
          }
        } catch (err: any) {
          message.error("Operation failed.");
        } finally {
          setOpen(false);
        }
      }

      refetch();
      setStageName(undefined);
      setStageNumber(undefined);
      setSelectedId(undefined);
      setOpen(false);
    }
  };

  const handleRemove = async () => {
    try {
      if (selectedId && product_id) {
        const result = await deleteStage(selectedId);

        if (result && result.status === 200) {
          message.success("Remove farming stage from product successfully.");
        }
      }
    } catch (err: any) {
      message.error("Operation failed.");
    } finally {
      setDeleteOpen(true);
    }
  };

  return (
    <div>
      <div
        style={{
          borderRadius: 8.5,
          marginTop: 25,
          paddingLeft: 50,
          paddingRight: 50,
          paddingTop: 22,
          backgroundColor: "white",
        }}
      >
        <div
          className="grid md:flex"
          style={{
            width: "100%",
            justifyContent: "space-between",
            marginBottom: 25,
          }}
        >
          <div className="w-full md:w-[45%]">
            <div
              style={{
                fontFamily: "Poppins",
                fontWeight: 700,
                borderRadius: "8.5px",
                backgroundColor: "white",
                paddingTop: "33px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#E9EDF4",
                  padding: "15px",
                  borderRadius: "8.5px",
                  fontWeight: 450,
                  fontSize: "24px",
                  marginBottom: "30px",
                }}
              >
                Product Detail
              </div>
              <div className="flex-col md:flex justify-between mb-5">
                <div style={wrapper}>
                  <div style={label}>Category</div>
                  <div style={value}>{product?.data?.data?.category}</div>
                </div>

                <div style={wrapper}>
                  <div style={label}>Product Name</div>
                  <div style={value}>{product?.data?.data?.productName}</div>
                </div>

                <div style={wrapper}>
                  <div style={label}>Variety</div>
                  <div style={value}>{product?.data?.data?.variety}</div>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 20,
              }}
            >
              <div className="flex-col md:wrapper">
                <div style={label}>Measurement Unit</div>
                <div style={value}>{product?.data?.data?.unit}</div>
              </div>
              <div style={wrapper}>
                <div style={label}>Description</div>
                <div style={value}>{product?.data?.data?.description}</div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-[50%]">
            <div
              style={{
                fontFamily: "Poppins",
                fontWeight: 700,
                borderRadius: "8.5px",
                backgroundColor: "white",
                paddingTop: "33px",
                paddingBottom: 33,
              }}
            >
              <div
                style={{
                  backgroundColor: "#E9EDF4",
                  padding: "15px",
                  borderRadius: "8.5px",
                  fontWeight: 500,
                  fontSize: "24px",
                  marginBottom: "30px",
                }}
              >
                Tags
              </div>
              {tags && tags.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #E6EEF6",
                    borderRadius: 4,
                    gap: 12,
                    paddingLeft: 50,
                    paddingRight: 50,
                    paddingTop: 0,
                    paddingBottom: 20,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      marginTop: -10,
                    }}
                  >
                    {tags &&
                      tags.length > 0 &&
                      Object.entries(tags[0]).map((tag: string[]) => {
                        return (
                          <div
                            key={Object.keys(tag)[0]}
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                              borderBottom: "1px solid #DCE2E4",
                              paddingTop: 20,
                              paddingBottom: 5,
                            }}
                          >
                            <div style={label}>{tag[0]}</div>
                            <div style={value}>{tag[1]}</div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
              {tags && tags.length === 0 && (
                <div className="flex justify-center text-[#8590A3] text-xl w-full">
                  No details available.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {user && user.roleName === "ROLE_KFT_SUPER_ADMIN" && (
        <>
          <div
            className="flex flex-col px-2 md:px-[50px] bg-transparent md:bg-white"
            style={{
              borderRadius: 8.5,
              marginTop: 25,
              paddingTop: 22,
              // backgroundColor: "white",
            }}
          >
            <Modal
              open={deleteOpen}
              onOk={handleRemove}
              onCancel={() => setDeleteOpen(false)}
              title={
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 20.43,
                    color: "black",
                  }}
                >
                  Confirmation
                </div>
              }
            >
              <div
                style={{
                  color: "black",
                }}
              >
                Are you sure you want to disassociate the selected farming
                stage?
              </div>
            </Modal>
            <Modal
              destroyOnClose={true}
              okButtonProps={{
                disabled: creating,
                loading: creating,
              }}
              title={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    fontSize: 27.43,
                    color: "black",
                  }}
                >
                  Add farming stage
                </div>
              }
              open={open}
              centered
              onOk={onSubmit}
              // okText={`${creating ? 'Saving...' : 'Ok'}`}
              onCancel={hideModal}
              closable
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <Select
                  value={stageId}
                  placeholder="Select region"
                  style={{ width: "100%" }}
                  onChange={(val: any) => setStageId(Number(val))}
                  options={fStages?.map((item: any) => {
                    return { label: item.name, value: item.id };
                  })}
                />
              </div>
            </Modal>
            <div className="hidden md:flex md:flex-col">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: 80,
                  backgroundColor: "#EEF3F8",
                  borderRadius: 8.4,
                  marginBottom: 12,
                }}
              >
                <TableAction
                  setSearchText={setSearchText}
                  onCreateNew={handleCreate}
                />
              </div>
              <Table
                style={{ cursor: "pointer" }}
                dataSource={tableData}
                columns={stagesColumn}
                loading={isLoading}
              />
            </div>
            <div className="w-full flex flex-col justify-between md:hidden">
              <div className="text-white text-2xl font-bold mb-8">
                <p>Farming Stages</p>
              </div>
              {tableData &&
                tableData.map((item: any) => {
                  return (
                    <Collapse
                      accordion
                      className="mb-1"
                      items={[
                        {
                          key: item.stageName,
                          label: item.stageName,
                          children: item.description,
                        },
                      ]}
                    />
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default WithAuth(ProductsDetailPage);
